import { integer, uuid, varchar } from 'drizzle-orm/pg-core/columns';
import { pgTable } from 'drizzle-orm/pg-core/table';
import { index, uniqueIndex } from 'drizzle-orm/pg-core/indexes';
import { foreignKey } from 'drizzle-orm/pg-core/foreign-keys';
import { commonFields } from './common-fields';
import { byteaAsHexString } from './types-bytea';
import { companies } from './companies';
import { users } from './users';
import { relations } from 'drizzle-orm/relations';
import { InferSelectModel } from 'drizzle-orm/table';
import {
  articlesOfAssocs,
  authorizationGroups,
  capTableStakes,
  companyDirectors,
  cooptedMeetingParticipants,
  issuanceProgramParticipants,
  meetingParticipants,
  niceFounders,
  optionsRegistrations,
  optionsRegistrationSignatories,
  optionsSharesRegistrations,
  optionsSharesSubscriberRepresentatives,
  optionsSharesSubscribers,
  optionsSubscriberRepresentatives,
  shareIssueBoardMembers,
  shareIssueParticipants,
  shareIssueRegistrations,
  shareIssues,
  todos,
  warrantBoardMembers,
} from './initial';
import { shareholders } from './shareholders';
import { warrantProgramParticipants, warrantPrograms, warrantShareholders } from './warrant_programs';
import { documentSignatures } from './document-signatures';

export type Identity = InferSelectModel<typeof identities>;

export const identities = pgTable(
  'identities',
  {
    ...commonFields,
    identityNumberKind: integer().notNull(),
    identityNumber: varchar().notNull(),
    juridicalKind: integer().notNull(),
    name: varchar().notNull(),
    email: varchar(),
    address: varchar(),
    // address2: varchar(),
    // city: varchar(),
    // postalCode: varchar(),
    countryCode: varchar(),
    companyId: uuid().notNull(),
    userId: uuid(),
    phone: varchar(),
    emailMd5: byteaAsHexString(),
  },
  table => {
    return [
      {
        idxIMidIid: uniqueIndex('idx_i_mid_iid').using(
          'btree',
          table.companyId.asc().nullsLast().op('text_ops'),
          table.identityNumberKind.asc().nullsLast().op('text_ops'),
          table.identityNumber.asc().nullsLast().op('int4_ops'),
        ),
        idxIdentityUserId: index('idx_identity_user_id').using('btree', table.userId.asc().nullsLast().op('uuid_ops')),
        identitiesCompanyIdFkey: foreignKey({
          columns: [table.companyId],
          foreignColumns: [companies.id],
          name: 'identities_company_id_fkey',
        }),
        identitiesUserIdFkey: foreignKey({
          columns: [table.userId],
          foreignColumns: [users.id],
          name: 'identities_user_id_fkey',
        }),
      },
    ];
  },
);

export const identitiesRelations = relations(identities, ({ one, many }) => ({
  articlesOfAssocs_contactId: many(articlesOfAssocs, {
    relationName: 'articlesOfAssocs_contactId_identities_id',
  }),
  articlesOfAssocs_signatoryId: many(articlesOfAssocs, {
    relationName: 'articlesOfAssocs_signatoryId_identities_id',
  }),
  authorizationGroups_contactId: many(authorizationGroups, {
    relationName: 'authorizationGroups_contactId_identities_id',
  }),
  authorizationGroups_signatoryId: many(authorizationGroups, {
    relationName: 'authorizationGroups_signatoryId_identities_id',
  }),
  capTableStakes: many(capTableStakes),
  companyDirectors: many(companyDirectors),
  cooptedMeetingParticipants: many(cooptedMeetingParticipants),
  documentSignatures_proxyId: many(documentSignatures, {
    relationName: 'documentSignatures_proxyId_identities_id',
  }),
  documentSignatures_signatoryId: many(documentSignatures, {
    relationName: 'documentSignatures_signatoryId_identities_id',
  }),
  company: one(companies, {
    fields: [identities.companyId],
    references: [companies.id],
  }),
  user: one(users, {
    fields: [identities.userId],
    references: [users.id],
  }),
  issuanceProgramParticipants: many(issuanceProgramParticipants),
  optionsRegistrationSignatories: many(optionsRegistrationSignatories),
  niceFounders: many(niceFounders),
  meetingParticipants_identityId: many(meetingParticipants, {
    relationName: 'meetingParticipants_identityId_identities_id',
  }),
  meetingParticipants_proxyId: many(meetingParticipants, {
    relationName: 'meetingParticipants_proxyId_identities_id',
  }),
  optionsSharesSubscriberRepresentatives: many(optionsSharesSubscriberRepresentatives),
  optionsSharesRegistrations_contactId: many(optionsSharesRegistrations, {
    relationName: 'optionsSharesRegistrations_contactId_identities_id',
  }),
  optionsSharesRegistrations_signatoryId: many(optionsSharesRegistrations, {
    relationName: 'optionsSharesRegistrations_signatoryId_identities_id',
  }),
  shareIssues_delContactId: many(shareIssues, {
    relationName: 'shareIssues_delContactId_identities_id',
  }),
  shareIssues_delSignatoryId: many(shareIssues, {
    relationName: 'shareIssues_delSignatoryId_identities_id',
  }),
  shareIssueBoardMembers: many(shareIssueBoardMembers),
  optionsSharesSubscribers: many(optionsSharesSubscribers),
  optionsSubscriberRepresentatives: many(optionsSubscriberRepresentatives),
  shareIssueRegistrations_contactId: many(shareIssueRegistrations, {
    relationName: 'shareIssueRegistrations_contactId_identities_id',
  }),
  shareIssueRegistrations_signatoryId: many(shareIssueRegistrations, {
    relationName: 'shareIssueRegistrations_signatoryId_identities_id',
  }),
  shareIssueParticipants_identityId: many(shareIssueParticipants, {
    relationName: 'shareIssueParticipants_identityId_identities_id',
  }),
  shareIssueParticipants_proxyId: many(shareIssueParticipants, {
    relationName: 'shareIssueParticipants_proxyId_identities_id',
  }),
  shareholders: many(shareholders),
  todos: many(todos),
  warrantPrograms_contactId: many(warrantPrograms, {
    relationName: 'warrantPrograms_contactId_identities_id',
  }),
  warrantPrograms_signatoryId: many(warrantPrograms, {
    relationName: 'warrantPrograms_signatoryId_identities_id',
  }),
  warrantShareholders: many(warrantShareholders),
  warrantBoardMembers: many(warrantBoardMembers),
  optionsRegistrations_contactId: many(optionsRegistrations, {
    relationName: 'optionsRegistrations_contactId_identities_id',
  }),
  optionsRegistrations_signatoryId: many(optionsRegistrations, {
    relationName: 'optionsRegistrations_signatoryId_identities_id',
  }),
  warrantProgramParticipants: many(warrantProgramParticipants),
}));
